/**
 * ============================================================================
 * API CONFIGURATION - AXIOS INSTANCE
 * ============================================================================
 * 
 * Centralized Axios configuration for all API requests
 * - Base URL configuration
 * - Request/Response interceptors
 * - Error handling
 * - Token management
 */

import axios from 'axios';
import useAuthStore from '../store/authStore';

// Base API URL (exported for refresh from APIProvider)
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api/v1';

// Create Axios instance (no default Content-Type so FormData can get multipart boundary)
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
});

// Request interceptor - Auth token and Content-Type for non-FormData
api.interceptors.request.use(
  (config) => {
    // FormData: do not set Content-Type (browser/axios set multipart/form-data with boundary)
    if (!config.data || typeof FormData === 'undefined' || !(config.data instanceof FormData)) {
      if (!config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }
    }

    const isRefresh = config.url?.includes('/auth/refresh');
    if (!isRefresh) {
      let token = typeof localStorage !== 'undefined' ? localStorage.getItem('accessToken') : null;
      if (!token && typeof localStorage !== 'undefined') {
        try {
          const raw = localStorage.getItem('auth-storage');
          if (raw) {
            const parsed = JSON.parse(raw);
            token = parsed?.state?.accessToken ?? parsed?.accessToken ?? null;
          }
        } catch (_) {}
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Add request ID for tracking
    config.headers['X-Request-ID'] = crypto.randomUUID();

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/** Get refresh token from localStorage, persisted auth-storage, or Zustand store */
export function getStoredRefreshToken() {
  try {
    const direct = localStorage.getItem('refreshToken');
    if (direct && direct.length > 0) return direct;
  } catch (_) {}
  try {
    const raw = localStorage.getItem('auth-storage');
    if (raw) {
      const parsed = JSON.parse(raw);
      const fromState = parsed?.state?.refreshToken;
      const fromTop = parsed?.refreshToken;
      const token = fromState || fromTop || null;
      if (token && token.length > 0) {
        localStorage.setItem('refreshToken', token);
        return token;
      }
    }
  } catch (_) {}
  try {
    const fromStore = useAuthStore.getState().refreshToken;
    if (fromStore && fromStore.length > 0) {
      localStorage.setItem('refreshToken', fromStore);
      return fromStore;
    }
  } catch (_) {}
  return null;
}

// Response interceptor - pass through; 401 refresh is handled in APIProvider (runs first, LIFO)
api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

// Helper function to handle API errors
export const handleApiError = (error) => {
  if (error.response) {
    const data = error.response.data;
    const err = data?.error || {};
    // Backend may put message in data.error.message (handleError) or data.message (validation)
    const message =
      err.message ||
      data?.message ||
      'An error occurred';
    return {
      message,
      errors: data?.errors || err.validationErrors || [],
      status: error.response.status,
      code: err.code ?? data?.code,
      expectedTotal: err.expectedTotal,
      overpayAmount: err.overpayAmount,
    };
  } else if (error.request) {
    // Request made but no response
    return {
      message: 'Network error. Please check your connection.',
      errors: [],
      status: 0,
    };
  } else {
    // Error in request setup
    return {
      message: error.message || 'An unexpected error occurred',
      errors: [],
      status: 0,
    };
  }
};

export default api;
